<template>
  <div class="spare-parts-tab">
    <!-- Total Number -->
    <div class="spr-total-row">
      <span class="spr-total-label">Total Number:</span>
      <span class="spr-total-value">{{ total }}</span>
      <span class="spr-total-unit">items</span>
    </div>

    <!-- Query Area -->
    <el-form :model="query" ref="queryForm" label-position="left" label-width="160px" class="spr-query" @keyup.enter.native="handleSearch">
      <el-row>
        <el-col :span="8">
          <el-form-item label="Project Name">
            <el-input clearable size="small" v-model="query.projectName" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Spare Model">
            <el-input clearable size="small" v-model="query.spareModel" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Spare SN">
            <el-input clearable size="small" v-model="query.spareSn" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="Fault Report SN / Service Request SN" class="spr-long-label">
            <el-input clearable size="small" v-model="query.faultReportSn" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Faulty Part Model">
            <el-input clearable size="small" v-model="query.faultyPartModel" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Shipment Date">
            <el-date-picker
              clearable
              size="small"
              v-model="query.shipmentDateRange"
              type="daterange"
              range-separator="—"
              start-placeholder="Start Date"
              end-placeholder="End Date"
              style="width:100%"
            ></el-date-picker>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="8">
          <el-form-item label="SLA">
            <el-select clearable size="small" v-model="query.sla" placeholder="Please select" style="width:100%">
              <el-option v-for="s in slaOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- Actions -->
        <el-col :span="16" class="spr-btn-col" style="padding-top: 3px;">
          <el-button type="primary" size="small" @click="handleSearch">Search</el-button>
          <el-button size="small" @click="handleReset">Reset</el-button>
          <el-button size="small" :loading="exportLoading" @click="handleExport">Export</el-button>
        </el-col>
      </el-row>
    </el-form>

    <!-- Table -->
    <el-table
      :data="tableData"
      v-loading="tableLoading"
      size="small"
      border
      style="width:100%"
      :header-cell-style="{background:'#F5F5F9'}"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="projectName" label="Project Name" min-width="200" sortable="custom" align="left" fixed="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="spareModel" label="Spare Model" min-width="170" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="spareSn" label="Spare SN" min-width="190" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="faultReportSn" label="Fault Report SN / Service Request SN" width="290" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="faultyPartModel" label="Faulty Part Model" min-width="170" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="shipmentDate" label="Shipment Date" width="145" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="sla" label="SLA" width="150" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="remark" label="Remark" min-width="160" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column label="Modify" width="110" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="handleModify(scope.row)">Modify</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <div class="spr-pagination">
      <span class="spr-pagination-total">Total {{ total }}</span>
      <el-pagination
        small
        layout="prev, pager, next, sizes"
        :total="total"
        :current-page.sync="currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="pageSize"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      ></el-pagination>
    </div>

    <!-- Modify Remark Modal -->
    <el-dialog title="Modify Remark" :visible.sync="modifyVisible" width="480px">
      <el-form label-position="left" label-width="70px">
        <el-form-item label="Remark:">
          <el-input type="textarea" :rows="4" v-model="modifyRemark" placeholder="Please input remark" maxlength="500" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="modifyVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="modifySaving" @click="saveModify" style="margin-left: 6px">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'SparePartsTab',
  props: {
    accountInfo: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      query: {
        projectName: '',
        spareModel: '',
        spareSn: '',
        faultReportSn: '',
        faultyPartModel: '',
        shipmentDateRange: null,
        sla: '',
      },
      slaOptions: [],
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      sortField: '',
      sortOrder: '',
      tableLoading: false,
      exportLoading: false,
      modifyVisible: false,
      modifyRow: null,
      modifyRemark: '',
      modifySaving: false,
    }
  },
  watch: {
    'accountInfo.id'(val) {
      if (val) {
        this.loadData()
      }
    }
  },
  created() {
    this.initDropdownData()
    if (this.accountInfo.id) {
      this.loadData()
    }
  },
  methods: {
    initDropdownData() {
      const kvUrl = `${this.$store.state.islhurl}keyValue/queryLabelList?keyList=SLA`
      this._get(kvUrl).then(res => {
        if (res.code === 200 && res.data) {
          this.slaOptions = res.data.SLA || []
        }
      }).catch(() => {})
    },
    loadData() {
      const id = this.accountInfo.id
      if (!id) return
      this.tableLoading = true
      const params = this.buildQueryParams()
      params.pageNum = this.currentPage
      params.pageSize = this.pageSize
      let url = `${this.$store.state.islhurl}valued-account/business/${id}/spare-parts`
      const qs = this.buildQueryString(params)
      if (qs) url += '?' + qs
      this._get(url).then(res => {
        this.tableLoading = false
        if (res.code === 200) {
          this.tableData = res.data || []
          this.total = res.totalSize || 0
        }
      }).catch(() => {
        this.tableLoading = false
      })
    },
    buildQueryParams() {
      const params = {}
      if (this.query.projectName) params.projectName = this.query.projectName
      if (this.query.spareModel) params.spareModel = this.query.spareModel
      if (this.query.spareSn) params.spareSn = this.query.spareSn
      if (this.query.faultReportSn) params.faultReportSn = this.query.faultReportSn
      if (this.query.faultyPartModel) params.faultyPartModel = this.query.faultyPartModel
      if (this.query.shipmentDateRange && this.query.shipmentDateRange.length === 2) {
        const [start, end] = this.query.shipmentDateRange
        params.shipmentDateStart = this.formatDate(start)
        params.shipmentDateEnd = this.formatDate(end)
      }
      if (this.query.sla) params.sla = this.query.sla
      if (this.sortField) {
        const fieldMap = {
          projectName: 'projectName',
          spareModel: 'spareModel',
          spareSn: 'spareSn',
          faultReportSn: 'faultReportSn',
          faultyPartModel: 'faultyPartModel',
          shipmentDate: 'shipmentDate',
          sla: 'sla',
          remark: 'remark',
        }
        params.sortField = fieldMap[this.sortField] || this.sortField
        params.sortOrder = this.sortOrder === 'ascending' ? 'asc' : 'desc'
      }
      return params
    },
    buildQueryString(params) {
      return Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&')
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.query = {
        projectName: '',
        spareModel: '',
        spareSn: '',
        faultReportSn: '',
        faultyPartModel: '',
        shipmentDateRange: null,
        sla: '',
      }
      this.sortField = ''
      this.sortOrder = ''
      this.currentPage = 1
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.sortField = order ? (prop || '') : ''
      this.sortOrder = order || ''
      this.currentPage = 1
      this.loadData()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadData()
    },
    handleModify(row) {
      this.modifyRow = row
      this.modifyRemark = row.remark || ''
      this.modifyVisible = true
    },
    saveModify() {
      if (!this.modifyRow || !this.modifyRow.sparePartId) return
      const id = this.accountInfo.id
      if (!id) return
      this.modifySaving = true
      const url = `${this.$store.state.islhurl}valued-account/business/${id}/spare-parts/${this.modifyRow.sparePartId}`
      this._put(url, { remark: this.modifyRemark }).then(res => {
        this.modifySaving = false
        if (res.code === 200) {
          this.$message.success('Remark updated successfully!')
          this.modifyVisible = false
          this.loadData()
        } else {
          this.$message.error(res.msg || 'Update failed!')
        }
      }).catch(() => {
        this.modifySaving = false
        this.$message.error('Update failed!')
      })
    },
    handleExport() {
      const id = this.accountInfo.id
      if (!id) return
      this.exportLoading = true
      const params = this.buildQueryParams()
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') delete params[key]
      })
      this.$axios({
        url: `${this.$store.state.islhurl}valued-account/business/${id}/spare-parts/export`,
        method: 'get',
        params,
        responseType: 'blob'
      }).then(response => {
        this.exportLoading = false
        const blob = new Blob([response.data])
        const aEle = document.createElement('a')
        const href = window.URL.createObjectURL(blob)
        aEle.href = href
        const d = new Date()
        const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
        aEle.download = 'Spare Parts Replacement_' + dateStr + '.xlsx'
        document.body.appendChild(aEle)
        aEle.click()
        document.body.removeChild(aEle)
        window.URL.revokeObjectURL(href)
      }).catch(() => {
        this.exportLoading = false
        this.$message.error('Export failed!')
      })
    },
  },
}
</script>

<style lang="less" scoped>
.spare-parts-tab {
  // Total Number
  .spr-total-row {
    padding: 0 0 4px 0;
    margin-bottom: 0;
    .spr-total-label {
      font-size: 13px;
      color: #6B7280;
      font-weight: 600;
    }
    .spr-total-value {
      font-size: 16px;
      color: #007AFF;
      font-weight: 700;
      margin: 0 2px;
    }
    .spr-total-unit {
      font-size: 12px;
      color: #6B7280;
    }
  }

  // Query
  .spr-query {
    padding: 8px 0 5px 0;
    /deep/ .el-col {
      margin-bottom: 7px;
    }
    /deep/ .el-form-item {
      margin-bottom: 0;
      padding: 0 15px 0 0;
    }
    /deep/ .el-form-item__label {
      font-size: 13px;
      color: #1F2937;
      text-align: right;
    }
    /deep/ .spr-long-label .el-form-item__label {
      line-height: 16px;
      white-space: normal;
      word-break: break-word;
      padding-top: 4px;
    }
    /deep/ .el-select {
      width: 100%;
    }
    /deep/ .el-form-item__content .el-input__suffix {
      left: auto;
      right: 10px;
    }
    /deep/ .el-range-editor.el-input__inner {
      width: 100%;
    }
  }
  .spr-btn-col {
    padding-top: 0;
    text-align: right;
    /deep/ .el-button + .el-button {
      margin-left: 10px;
    }
  }

  // Table Tags
  .spr-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 12px;
    line-height: 20px;
    &.spr-tag-red { background: #fff1f0; color: #F5222D; border: 1px solid #ffa39e; }
    &.spr-tag-orange { background: #fff7e6; color: #FA8C16; border: 1px solid #ffd591; }
    &.spr-tag-blue { background: #e6f7ff; color: #1890FF; border: 1px solid #91d5ff; }
    &.spr-tag-gray { background: #fafafa; color: #8C8C8C; border: 1px solid #d9d9d9; }
  }

  // Pagination
  .spr-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 0 0;
    gap: 12px;
    .spr-pagination-total {
      font-size: 13px;
      color: #6B7280;
    }
  }
}
.el-button+.el-button, .el-checkbox.is-bordered+.el-checkbox.is-bordered {
  margin-left: 0;
}
</style>



// WEBPACK FOOTER //
// src/view/valuedAccountManagement/accountDetailTabs/SpareParts.vue